import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useCases } from "@/hooks/useCases";
import { getFlag } from "@/lib/countryIndex";
import { buildSeo } from "@/lib/seo";

type Search = {
  theme?: "dark" | "light";
  compact?: boolean;
  limit?: number;
};

export const Route = createFileRoute("/embed")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    theme: s.theme === "light" ? "light" : "dark",
    compact: s.compact === true || s.compact === "1" || s.compact === "true",
    limit: typeof s.limit === "string" ? Number(s.limit) || 5 : (s.limit as number) ?? 5,
  }),
  head: () =>
    buildSeo({
      path: "/embed",
      title: "Hantavirus Tracker — Embeddable Widget",
      description:
        "A lightweight, embeddable hantavirus outbreak widget for news, public-health and travel sites.",
    }),
  component: EmbedWidget,
});

function EmbedWidget() {
  const { theme, compact, limit } = Route.useSearch();
  const { cases, loading } = useCases();

  const stats = useMemo(() => {
    const totals = { cases: cases.length, deaths: 0, confirmed: 0, countries: new Set<string>() };
    const byCountry = new Map<string, { cases: number; deaths: number }>();
    for (const c of cases) {
      totals.countries.add(c.country);
      if (c.status === "fatal") totals.deaths += 1;
      if (c.status === "confirmed") totals.confirmed += 1;
      const cur = byCountry.get(c.country) ?? { cases: 0, deaths: 0 };
      cur.cases += 1;
      if (c.status === "fatal") cur.deaths += 1;
      byCountry.set(c.country, cur);
    }
    const top = [...byCountry.entries()]
      .sort((a, b) => b[1].cases - a[1].cases)
      .slice(0, Math.max(1, Math.min(limit ?? 5, 10)));
    return { totals, top };
  }, [cases, limit]);

  const isDark = theme !== "light";
  const palette = isDark
    ? {
        bg: "#0b0d10",
        surface: "#14181d",
        border: "#262b32",
        text: "#e6e8eb",
        muted: "#8a929c",
        accent: "#ef4444",
        accent2: "#f97316",
      }
    : {
        bg: "#ffffff",
        surface: "#f7f8fa",
        border: "#e5e7eb",
        text: "#0b0d10",
        muted: "#6b7280",
        accent: "#dc2626",
        accent2: "#ea580c",
      };

  return (
    <div
      style={{
        background: palette.bg,
        color: palette.text,
        fontFamily:
          "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        minHeight: "100vh",
        padding: compact ? 12 : 16,
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: compact ? 10 : 14, gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: palette.accent, display: "inline-block", boxShadow: `0 0 0 4px ${palette.accent}22` }} />
          <span style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: palette.muted, fontWeight: 700 }}>
            Hantavirus · Live
          </span>
        </div>
        <a
          href="https://hantavirus.international/?utm_source=embed"
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: 10, color: palette.muted, textDecoration: "none", letterSpacing: "0.12em", textTransform: "uppercase" }}
        >
          hantavirus.international ↗
        </a>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: compact ? 10 : 14 }}>
        <Stat palette={palette} label="Total cases" value={stats.totals.cases} accent={palette.accent2} loading={loading} />
        <Stat palette={palette} label="Deaths" value={stats.totals.deaths} accent={palette.accent} loading={loading} />
        <Stat palette={palette} label="Countries" value={stats.totals.countries.size} accent={palette.muted} loading={loading} />
      </div>

      {!compact && (
        <div style={{ border: `1px solid ${palette.border}`, borderRadius: 6, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 64px 64px", padding: "8px 10px", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: palette.muted, background: palette.surface, fontWeight: 700 }}>
            <span>Country</span>
            <span style={{ textAlign: "right" }}>Cases</span>
            <span style={{ textAlign: "right" }}>Deaths</span>
          </div>
          {stats.top.map(([country, d]) => (
            <a
              key={country}
              href={`https://hantavirus.international/?utm_source=embed#regions`}
              target="_blank"
              rel="noreferrer"
              style={{ display: "grid", gridTemplateColumns: "1fr 64px 64px", padding: "9px 10px", fontSize: 13, borderTop: `1px solid ${palette.border}`, color: palette.text, textDecoration: "none" }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <span>{getFlag(country)}</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{country}</span>
              </span>
              <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: palette.accent2, fontWeight: 600 }}>{d.cases}</span>
              <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: d.deaths > 0 ? palette.accent : palette.muted, fontWeight: 600 }}>{d.deaths}</span>
            </a>
          ))}
        </div>
      )}

      <div style={{ marginTop: compact ? 8 : 12, fontSize: 10, color: palette.muted, textAlign: "center" }}>
        Powered by{" "}
        <a href="https://hantavirus.international/?utm_source=embed" target="_blank" rel="noreferrer" style={{ color: palette.accent, textDecoration: "none", fontWeight: 600 }}>
          Hantavirus International
        </a>
      </div>
    </div>
  );
}

function Stat({
  palette,
  label,
  value,
  accent,
  loading,
}: {
  palette: { surface: string; border: string; text: string; muted: string };
  label: string;
  value: number;
  accent: string;
  loading: boolean;
}) {
  return (
    <div style={{ background: palette.surface, border: `1px solid ${palette.border}`, borderLeft: `3px solid ${accent}`, borderRadius: 4, padding: "8px 10px" }}>
      <div style={{ fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase", color: palette.muted, fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, marginTop: 2, fontVariantNumeric: "tabular-nums", color: palette.text }}>
        {loading ? "—" : value.toLocaleString()}
      </div>
    </div>
  );
}
